/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';

import { initGraphQlClients, addIdentifiers } from '../../utils/initConfigs';

export const ConfigContext = React.createContext();

const prepareConfigs = configs => configs.map(initGraphQlClients).map(addIdentifiers);
const createState = givenConfigs => ({ givenConfigs, preparedConfigs: prepareConfigs(givenConfigs) });
// If the givenConfigs haven't changed, do not prepare them again. This is necessary because we cannot initialize
// the state with an empty array of configs. Some components like the CategoryPickerContextProvider do not support this (yet).
const reducer = (oldState, newConfigs) => (oldState.givenConfigs === newConfigs ? oldState : createState(newConfigs));

const ConfigContextProvider = props => {
    // Inject development configurations in webpack development mode
    const [configs, setConfigs] = useReducer(reducer, process.env.DEV_CONFIGS || props.configs, createState);

    useEffect(() => {
        if (!process.env.DEV_CONFIGS) {
            setConfigs(props.configs);
        }
    }, [props.configs]);

    return <ConfigContext.Provider value={[configs.preparedConfigs]}>{props.children}</ConfigContext.Provider>;
};

ConfigContextProvider.propTypes = {
    configs: PropTypes.arrayOf(PropTypes.object)
};

export default ConfigContextProvider;

export const useConfigContext = () => useContext(ConfigContext);
